import React from 'react';
import { Box, Typography, Paper, Divider, Container, Alert, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { DeleteForever, Warning, Email, Security, Info } from '@mui/icons-material';

export default function DeleteAccount() {
  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      py: 4
    }}>
      <Container maxWidth="md">
        <Paper elevation={8} sx={{ 
          p: { xs: 3, md: 6 }, 
          borderRadius: 3,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)'
        }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h3" gutterBottom sx={{ 
              color: '#2c3e50', 
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}>
              Delete Account
            </Typography>
            <Typography variant="h6" sx={{ color: '#7f8c8d', fontWeight: 400 }}>
              How to permanently delete your account
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Warning Alert */}
          <Alert severity="warning" sx={{ mb: 4, fontSize: '1.1rem' }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              This action is irreversible. Once your account is deleted, all your data will be permanently removed.
            </Typography>
          </Alert>

          {/* App/Developer Name */}
          <Box sx={{ mb: 4 }}>
            
            <Typography variant="body1" sx={{ color: '#34495e', mb: 2 }}>
              <strong>App Name:</strong> Ascend Yantra Meditation
            </Typography>
            <Typography variant="body1" sx={{ color: '#34495e', mb: 2 }}>
              <strong>Developer:</strong> Flowxperience Inc.
            </Typography>
            <Typography variant="body1" sx={{ color: '#34495e' }}>
              <strong>Contact Email:</strong> support@ascendyantra.com
            </Typography>
          </Box>

          {/* Steps to Delete Account from Within the App */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ 
              color: '#2c3e50', 
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 2
            }}>
              <Security sx={{ color: '#d32f2f' }} />
              Steps to Delete Account from Within the App
            </Typography>
            
            <List>
              <ListItem>
                <ListItemIcon>
                  <DeleteForever sx={{ color: '#d32f2f' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="1. Login"
                  secondary="Sign in to your account which you want to delete"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <DeleteForever sx={{ color: '#d32f2f' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="2. Go to Profile"
                  secondary="Navigate to your profile section"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <DeleteForever sx={{ color: '#d32f2f' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="3. Click on 'Delete Account'"
                  secondary="Find and click the delete account option at the bottom of the profile page"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <DeleteForever sx={{ color: '#d32f2f' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="4. Delete"
                  secondary="Click on delete button of the dialog box"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <DeleteForever sx={{ color: '#d32f2f' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="5. Confirm"
                  secondary="Confirm your account deletion"
                />
              </ListItem>
            </List>
          </Box>

          {/* What Happens to User Data */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ 
              color: '#2c3e50', 
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 2
            }}>
              <Email sx={{ color: '#d32f2f' }} />
              What Happens to User Data
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#34495e', fontWeight: 600 }}>
                Data Retention Period
              </Typography>
              <Typography variant="body1" sx={{ color: '#34495e', mb: 2 }}>
                When you request account deletion, your data will be retained for a period of 30 days before permanent removal.
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#34495e', fontWeight: 600 }}>
                Permanent Removal
              </Typography>
              <Typography variant="body1" sx={{ color: '#34495e', mb: 2 }}>
                After 30 days, your account and all associated data will be permanently deleted and cannot be recovered. You will receive a confirmation mail about the deletion of your account. This includes:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <Typography component="li" sx={{ color: '#34495e', mb: 1 }}>
                  Personal information (name, email, profile data)
                </Typography>
                <Typography component="li" sx={{ color: '#34495e', mb: 1 }}>
                  Meditation history and progress tracking
                </Typography>
                <Typography component="li" sx={{ color: '#34495e', mb: 1 }}>
                  App preferences and settings
                </Typography>
                <Typography component="li" sx={{ color: '#34495e', mb: 1 }}>
                  Subscription and payment information
                </Typography>
              </Box>
            </Box>

            
          </Box>

          {/* Footer */}
          <Divider sx={{ mb: 4 }} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#7f8c8d' }}>
              If you have any questions about the account deletion process, please contact us at support@ascendyantra.com
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
} 